import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Square } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { dateTime, money, shortId } from "@/lib/format";
import { label } from "@/lib/labels";

interface Row {
  id: string;
  status: "proposed" | "active" | "paused" | "ended" | "declined";
  frequency: "weekly" | "biweekly" | "monthly";
  nextStart: string | null;
  hourlyRate: { amountMinor: string; currency: string } | null;
  proposedHourlyRate: { amountMinor: string; currency: string } | null;
  templateTaskId: string;
  endReason: string | null;
  createdAt: string | null;
  service: string;
  providerId: string;
  provider: string;
  customerId: string;
  customerEmail: string | null;
  tasks: number;
}

const FREQ: Record<Row["frequency"], string> = {
  weekly: "Her hafta",
  biweekly: "2 haftada bir",
  monthly: "Her ay",
};
const STATUS: Record<
  Row["status"],
  { text: string; variant: "default" | "secondary" | "outline" }
> = {
  proposed: { text: "Onay bekliyor", variant: "outline" },
  active: { text: "Aktif", variant: "default" },
  paused: { text: "Durduruldu", variant: "secondary" },
  ended: { text: "Bitti", variant: "secondary" },
  declined: { text: "Reddedildi", variant: "secondary" },
};
const END: Record<string, string> = {
  customer: "müşteri bitirdi",
  provider: "hizmet veren bitirdi",
  admin: "panelden bitirildi",
  payment_method: "kart yok",
  provider_inactive: "hizmet veren çalışmıyor",
};

function End({ row }: { row: Row }) {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState("");
  return (
    <ActionDialog
      onOpen={() => setReason("")}
      trigger={
        <Button variant="ghost" size="sm">
          <Square /> Bitir
        </Button>
      }
      title="Düzenli rezervasyonu bitir"
      description="Yeni iş oluşmaz; daha önce oluşmuş işler kalır (gerekirse ayrıca iptal edilir). İki taraf bildirim alır."
      confirmLabel="Bitir"
      canSubmit={reason.trim().length >= 3}
      success="Düzenli rezervasyon bitirildi."
      onSubmit={async (key) => {
        await api(`/v1/admin/tasks/series/${row.id}/end`, {
          method: "POST",
          idempotencyKey: key,
          body: { reason: reason.trim() },
        });
        await qc.invalidateQueries({ queryKey: ["admin-series"] });
      }}
    >
      <div className="grid gap-2">
        <Label htmlFor="series-reason">Gerekçe (denetim kaydına yazılır)</Label>
        <Textarea id="series-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
    </ActionDialog>
  );
}

export function SeriesPage() {
  const { can } = useAuth();
  const [status, setStatus] = React.useState("active");
  const q = useQuery({
    queryKey: ["admin-series", status],
    queryFn: () =>
      api<{ items: Row[] }>(`/v1/admin/tasks/series${status ? `?status=${status}` : ""}`),
  });
  const columns: ColumnDef<Row, unknown>[] = [
    {
      id: "service",
      header: "Hizmet",
      cell: ({ row }) => (
        <Link
          className="text-primary hover:underline"
          to={`/tasks/${row.original.templateTaskId}`}
          title="Seriyi kuran iş"
        >
          {label(row.original.service)}{" "}
          <span className="font-mono text-xs text-muted-foreground">
            {shortId(row.original.id)}
          </span>
        </Link>
      ),
    },
    {
      id: "who",
      header: "Müşteri → Hizmet veren",
      cell: ({ row }) => (
        <div className="text-sm">
          <Link className="hover:underline" to={`/users/${row.original.customerId}`}>
            {row.original.customerEmail ?? shortId(row.original.customerId)}
          </Link>
          <span className="text-muted-foreground"> → </span>
          <Link className="hover:underline" to={`/providers/${row.original.providerId}`}>
            {row.original.provider}
          </Link>
        </div>
      ),
    },
    {
      id: "frequency",
      header: "Sıklık",
      cell: ({ row }) => FREQ[row.original.frequency],
    },
    {
      id: "status",
      header: "Durum",
      cell: ({ row }) => {
        const s = STATUS[row.original.status];
        return (
          <div>
            <Badge variant={s.variant}>{s.text}</Badge>
            {row.original.endReason && (
              <div className="text-xs text-muted-foreground">
                {END[row.original.endReason] ?? row.original.endReason}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "price",
      header: "Saatlik (komisyonsuz)",
      cell: ({ row }) => (
        <div>
          {money(row.original.hourlyRate)}
          {row.original.proposedHourlyRate && (
            <div className="text-xs text-muted-foreground">
              önerilen {money(row.original.proposedHourlyRate)}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "next",
      header: "Sıradaki",
      cell: ({ row }) =>
        row.original.status === "active" || row.original.status === "paused"
          ? dateTime(row.original.nextStart)
          : "—",
    },
    { id: "tasks", header: "Oluşan iş", cell: ({ row }) => row.original.tasks },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        can("tasks.manage") && !["ended", "declined"].includes(row.original.status) ? (
          <div className="flex justify-end">
            <End row={row.original} />
          </div>
        ) : null,
    },
  ];
  return (
    <>
      <PageHeader
        title="Düzenli işler"
        description="Müşterinin aynı hizmet verenle kurduğu düzenli rezervasyonlar (K-118). Her iş başlangıcından 7 gün önce kendiliğinden oluşur, ödeme her iş için ayrı. Fiyat değişikliği müşteri onayıyla."
        actions={
          <NativeSelect
            aria-label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="active">Aktif</option>
            <option value="proposed">Onay bekleyen</option>
            <option value="paused">Durdurulmuş</option>
            <option value="ended">Biten</option>
            <option value="">Tümü</option>
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.items}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Bu durumda düzenli rezervasyon yok."
      />
    </>
  );
}
